import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { loadActionPlanStatusDetails } from '../../../store/directusService';
import { createActionPlan } from '../../../store/participantService';
import { getNameFromId } from '../../../utils/directusFunctions';
import { clearKeys } from '../../../utils/objectUtils';
import { hasRole, QUALITY, SUPERUSER } from '../../../utils/userRoles';
import DateSelect from '../../formElements/DateSelect';
import RadioButtons from '../../formElements/RadioButtons';
import SingleSelect from '../../formElements/SingleSelect';
import TextAreaField from '../../formElements/TextAreaField';
import TextInputField from '../../formElements/TextInputField';
import FormActions from '../../ui/formActions/FormActions';
import SmartDefs from '../../ui/notices/smartDefs/SmartDefs';

import { calcDays } from './utils';
import { validate } from './validateCreateActionPlan';
import {
    MAX_ACTION_COMMENTS,
    MAX_ACTION_DESCRIPTION,
    MAX_ACTION_TITLE
} from './validationFunctions';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';
import classes from './actionPlan.module.css';

const outcomeDateEditRoles = [QUALITY, SUPERUSER];

const CreateActionPlan = ({ onClose, roles, isDisabled, isViewOnly }) => {
    const dispatch = useDispatch();
    // LOCAL STATE
    const initialState = {
        id: '',
        participantId: '',
        mandatory: false,
        actionTitle: '',
        action: '',
        barrierId: '',
        actionStartDate: new Date().toISOString().slice(0, 10),
        actionDueDate: '',
        actionOutcomeDate: '',
        totalDays: null,
        outcomeId: '',
        outcomeName: '',
        moduleId: null,
        comments: ''
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [allowMandatoryActivities, setAllowMandatoryActivities] = useState(true);
    const [outcomes, setOutcomes] = useState([]);
    const [keys, setKeys] = useState({ barrierId: '0', outcome: '1' });
    const [errors, setErrors] = useState({});

    const [currentParticipantBarriersWithNames, setCurrentParticipantBarriersWithNames] = useState(
        []
    );
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [isOutcomeDateDisabled, setIsOutcomeDateDisabled] = useState(true);

    // STORE STATE
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const currentParticipantBarriers = useSelector(
        (state) => state.entities.participantService.currentParticipantBarriers
    );
    const actionPlanStatuses = useSelector(
        (state) => state.entities.directusService.actionPlanStatusDetails
    );
    const contractConstants = useSelector(
        (state) => state.entities.participantService.contractConstants
    );
    const barrierNameDetails = useSelector(
        (state) => state.entities.directusService.barrierNameDetails
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        if (!actionPlanStatuses?.length) dispatch(loadActionPlanStatusDetails());
    }, []);

    useEffect(() => {
        setIsOutcomeDateDisabled(
            !hasRole(outcomeDateEditRoles, roles) || isViewOnly || !newEntry.outcomeId
        );
    }, [roles, newEntry.outcomeId]);

    useEffect(() => {
        if (currentParticipantBarriers?.length < 1) return;
        setCurrentParticipantBarriersWithNames(
            currentParticipantBarriers.map((el) => ({
                ...el,
                name: getNameFromId(barrierNameDetails, el.barrierId)
            }))
        );
    }, [currentParticipantBarriers]);

    useEffect(() => {
        if (!currentParticipant?.id) return;
        setAllowMandatoryActivities(currentParticipant?.allowMandatoryActivities || false);
    }, [currentParticipant?.id]);

    useEffect(() => {
        if (!actionPlanStatuses?.length) return;
        setOutcomes(
            actionPlanStatuses.filter((el) =>
                el.contract.some((entry) => entry.contract_id.id === currentParticipant.contractId)
            )
        );
    }, [actionPlanStatuses]);

    useEffect(() => {
        if (newEntry.outcomeId)
            setNewEntry((prev) => ({
                ...prev,
                actionOutcomeDate: new Date().toISOString().slice(0, 10)
            }));
    }, [newEntry.outcomeId]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === 'Participant action plan has been added') clearForm();
    }, [successMessage]);

    // HELPER FNS
    const clearForm = () => {
        setNewEntry(initialState);
        setKeys(clearKeys(keys));
        setIsButtonDisabled(false);
    };

    const clearError = (key) => {
        if (key in errors) setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onFormExit = () => {
        clearForm();
        onClose();
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const newErrors = validate(newEntry, contractConstants.endDate);
        if (Object.values(newErrors).some((el) => el.error)) {
            setErrors(newErrors);
            return;
        }
        // eslint-disable-next-line
        const { id, outcomeName, totalDays, ...rest } = newEntry;
        const payload = { ...rest, participantId: currentParticipant.id };
        dispatch(createActionPlan(payload));
        onFormExit();
    };

    // RENDER
    return (
        <form onSubmit={onSubmit} data-testid="form_start_create_action_plan">
            <h2 className={app.sectionHeading}>Create an Action</h2>
            <div className={form.formSection}>
                <div className={form.formColumn}>
                    <RadioButtons
                        id="mandatory"
                        label="Is this a mandatory action?"
                        disabled={isDisabled || !allowMandatoryActivities}
                        value={newEntry.mandatory ?? false}
                        onChange={(option) => onUpdate('mandatory', option)}
                    />
                    <TextInputField
                        id="actionTitle"
                        label="What shall we call this action?"
                        placeholder="Enter an action name..."
                        value={newEntry.actionTitle || ''}
                        mandatory={true}
                        disabled={isDisabled}
                        maxLength={MAX_ACTION_TITLE}
                        error={errors.actionTitle}
                        onChange={(e) => onUpdate('actionTitle', e.target.value)}
                    />
                    <SingleSelect
                        id="barrierId"
                        key={keys.barrierId}
                        label="Which barrier does this action align to?"
                        placeholder="Select a barrier..."
                        disabled={isDisabled}
                        menuItems={currentParticipantBarriersWithNames || []}
                        selectedId={newEntry.barrierId || ''}
                        selected={currentParticipantBarriersWithNames.find(
                            (el) => el.id === newEntry.barrierId
                        )}
                        onChange={(chosenId) => onUpdate('barrierId', chosenId)}
                    />
                    <div className={classes.smartDefsWrapper}>
                        <div className={classes.heading}>What is the action?</div>
                        <SmartDefs />
                    </div>
                    <TextAreaField
                        id="action"
                        label="Action"
                        placeholder="Enter action description..."
                        disabled={isDisabled}
                        mandatory={true}
                        maxLength={MAX_ACTION_DESCRIPTION}
                        value={newEntry.action || ''}
                        count={`${newEntry.action.length}/${MAX_ACTION_DESCRIPTION}`}
                        error={errors.action}
                        onChange={(e) => onUpdate('action', e.target.value)}
                    />
                </div>
                <div className={form.formColumn}>
                    <div className={form.formColumnSplit}>
                        <DateSelect
                            id="actionStartDate"
                            label="Action Start Date"
                            disabled={true}
                            value={newEntry.actionStartDate}
                        />
                        <DateSelect
                            id="actionDueDate"
                            label="Action Due Date"
                            disabled={isDisabled}
                            value={newEntry.actionDueDate}
                            error={errors.actionDueDate}
                            onDateChange={(date) => onUpdate('actionDueDate', date)}
                        />
                    </div>
                    <h2 className={app.sectionHeading}>Action completion information</h2>
                    <div className={form.formColumnSplit}>
                        <DateSelect
                            id="actionOutcomeDate"
                            label="Action Outcome Date"
                            disabled={isOutcomeDateDisabled}
                            value={newEntry.actionOutcomeDate}
                            isDefault={false}
                            error={errors.actionOutcomeDate}
                            onDateChange={(date) => onUpdate('actionOutcomeDate', date)}
                        />

                        <div className={classes.inputSet} data-testid="days_to_complete">
                            <label className={form.formLabel}>Total Days to Complete</label>
                            <div className={classes.calculatedInputBox}>
                                {calcDays(actionPlanStatuses, newEntry)}
                            </div>
                        </div>
                    </div>
                    <SingleSelect
                        id="outcome"
                        key={keys.outcome}
                        label="Outcome"
                        placeholder="Select an outcome..."
                        disabled={isDisabled}
                        menuItems={outcomes || []}
                        selectedId={newEntry.outcomeId}
                        selected={outcomes.find((el) => el.id === newEntry.outcomeId)}
                        onChange={(chosenId) => onUpdate('outcomeId', chosenId)}
                    />
                    <TextAreaField
                        id="comments"
                        label="Comments"
                        placeholder="Enter comments here..."
                        subLabel="Use this area for comments surrounding this action"
                        disabled={isDisabled}
                        maxLength={MAX_ACTION_COMMENTS}
                        value={newEntry.comments}
                        counter={'true'}
                        count={`${newEntry.comments.length}/${MAX_ACTION_COMMENTS}`}
                        error={errors.comments}
                        onChange={(e) => onUpdate('comments', e.target.value)}
                    />
                </div>
            </div>
            <FormActions
                onClose={onSubmit}
                onCancel={onFormExit}
                btnText="Add Action"
                disabled={isButtonDisabled || isDisabled}
            />
        </form>
    );
};

CreateActionPlan.propTypes = {
    onClose: PropTypes.func,
    roles: PropTypes.arrayOf(PropTypes.string),
    isDisabled: PropTypes.bool,
    isViewOnly: PropTypes.bool
};

export default CreateActionPlan;
